// Chip — free-form metadata / action pill (icon + label, optional remove ×).
// i18n-clean: every user-facing string (label, `removeLabel`) arrives via props;
// the primitive imports only from `./` (ui), never a feature.
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { Icon, type IconName } from './Icon.js';

// Discriminated union: `removeLabel` is type-required whenever `onRemove` is set.
export type ChipProps = {
  icon?: IconName;
  children: ReactNode;
  onClick?: () => void;
  title?: string;
  tone?: 'neutral' | 'accent';
} & (
  | { onRemove?: undefined; removeLabel?: undefined }
  | { onRemove: () => void; removeLabel: string }
);

// Transparent inner button used when the chip is BOTH clickable and removable,
// so the label and the × render as sibling buttons (never nested buttons).
const LABEL_BUTTON: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'transparent',
  border: 0,
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export function Chip(props: ChipProps) {
  const { icon, children, onClick, title, tone = 'neutral', onRemove, removeLabel } = props;
  const [hover, setHover] = useState(false);
  const clickable = onClick !== undefined;

  const background =
    clickable && hover
      ? 'var(--bg-2)'
      : tone === 'accent'
        ? 'var(--accent-1-subtle)'
        : 'var(--bg-1)';

  const pill: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 22,
    padding: '0 10px',
    borderRadius: 'var(--r-full)',
    border: '1px solid var(--border-1)',
    background,
    fontSize: 'var(--fz-12)',
    color: 'var(--fg-2)',
    whiteSpace: 'nowrap',
  };

  const content = (
    <>
      {icon && <Icon name={icon} size={12} style={{ color: 'var(--fg-3)' }} />}
      {children}
    </>
  );

  let removeControl: ReactNode = null;
  if (onRemove) {
    const doRemove = onRemove;
    removeControl = (
      <button
        type="button"
        aria-label={removeLabel}
        // WHY: stop the × click from bubbling to the chip's own onClick.
        onClick={(e) => {
          e.stopPropagation();
          doRemove();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          background: 'transparent',
          border: 0,
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          color: 'var(--fg-3)',
          lineHeight: 0,
        }}
      >
        <Icon name="x" size={12} />
      </button>
    );
  }

  const enter = () => setHover(true);
  const leave = () => setHover(false);

  // Clickable AND removable: label + × are SIBLING buttons inside a span pill.
  if (clickable && onRemove) {
    return (
      <span style={pill} title={title} onMouseEnter={enter} onMouseLeave={leave}>
        <button type="button" onClick={onClick} style={LABEL_BUTTON}>
          {content}
        </button>
        {removeControl}
      </span>
    );
  }

  // Clickable only: the whole chip is a single button.
  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        style={{ ...pill, cursor: 'pointer' }}
        onMouseEnter={enter}
        onMouseLeave={leave}
      >
        {content}
      </button>
    );
  }

  // Static, or removable-only: a span pill (with the × button when removable).
  return (
    <span style={pill} title={title}>
      {content}
      {removeControl}
    </span>
  );
}
